import {
  adminHofPrizeSchema,
  HOF_ICON_LABELS,
  HOF_ICONS,
  HOF_LEGACY_URL,
  hofAccountSchema,
  hofIconSchema,
  hofImportResultSchema,
  type AdminHofPrize,
  type HofAccount,
  type HofIcon,
  type HofImportResult,
} from '@growup/site-shared';
import {
  Autocomplete,
  Button,
  Card,
  Field,
  HofMedal,
  Input,
  moduleOrigins,
  Notice,
  Select,
  StatusBadge,
  Textarea,
} from '@growup/ui';
import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { ImageField, useImagesBusy } from '../media/ImageUpload';

const BASE = '/api/site/hall-of-fame';

const listSchema = z.array(adminHofPrizeSchema);

/** Mesmo ano: so ai a ordem a mao conta. */
const sameSeason = (a: AdminHofPrize | undefined, b: AdminHofPrize | undefined) =>
  a !== undefined && b !== undefined && a.season === b.season;

/** O vencedor como aparece na lista: o nome escrito e a conta ligada. */
function winnerLabel(p: AdminHofPrize): string {
  const name = p.winnerName || '(sem vencedor)';
  if (p.accountId === null) return name;
  return p.login && p.login.toLowerCase() !== p.winnerName.toLowerCase()
    ? `${name} · ${p.login}`
    : name;
}

/**
 * Hall of Fame Grow uP (uP Awards) em Conteudos > Premios: os premios que a
 * associacao da aos seus membros (docs/hall-of-fame). Uma lista simples por
 * ano, como os Premios recebidos (pedido pelo GrowuP a 20-10-2026). Os
 * vencedores escolhe-os a direcao, fora do sistema; aqui so se registam.
 */
export function HallOfFameView() {
  const [list, setList] = useState<AdminHofPrize[] | null>(null);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  // Muda so ao abrir o editor: criar um premio nao o volta a montar.
  const [opened, setOpened] = useState(0);
  const open = (id: string | 'new') => {
    setOpened((n) => n + 1);
    setEditing(id);
  };
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getJson(BASE, listSchema)
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const replace = (saved: AdminHofPrize) =>
    setList((l) => {
      if (!l) return [saved];
      return l.some((p) => p.id === saved.id)
        ? l.map((p) => (p.id === saved.id ? saved : p))
        : [...l, saved];
    });

  const swap = async (i: number, j: number) => {
    if (!list) return;
    const ids = list.map((p) => p.id);
    const a = ids[i];
    const b = ids[j];
    if (a === undefined || b === undefined) return;
    ids[i] = b;
    ids[j] = a;
    setBusy(true);
    setError(null);
    try {
      setList(listSchema.parse(await send('PUT', `${BASE}/order`, { ids })));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  if (importing) {
    return (
      <ImportView
        onClose={() => {
          setImporting(false);
          load();
        }}
      />
    );
  }

  if (editing !== null) {
    return (
      <PrizeEditor
        key={opened}
        prize={list?.find((p) => p.id === editing) ?? null}
        names={[...new Set(list?.map((p) => p.name) ?? [])]}
        onSaved={(saved) => {
          replace(saved);
          setEditing(saved.id);
        }}
        onClose={() => {
          setEditing(null);
          load();
        }}
      />
    );
  }

  const claims = list?.filter((p) => p.claim !== null) ?? [];
  const published = list?.filter((p) => p.status === 'published').length ?? 0;
  const unlinked = list?.filter((p) => p.accountId === null && p.winnerName !== '').length ?? 0;
  let lastSeason: string | null = null;

  return (
    <div className="flex flex-col gap-4">
      {claims.length > 0 ? (
        <ClaimsCard claims={claims} onDone={(saved) => replace(saved)} onError={setError} />
      ) : null}
      <Card
        headingLevel={2}
        title="Hall of Fame"
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => setImporting(true)}>
              Importar do site antigo
            </Button>
            <Button size="sm" onClick={() => open('new')}>
              Novo prémio
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-gu-ink-muted">
            Os prémios que a Grow uP dá aos membros, em growup.gg/hall-of-fame. Cada linha é um
            prémio dado a uma pessoa; dois vencedores do mesmo prémio são duas linhas com o mesmo
            ano e nome. Ligue cada vencedor à sua conta para o prémio aparecer como medalha na carta
            de membro. As setas mudam a ordem dentro do mesmo ano.{' '}
            {list
              ? `${String(published)} no site agora, ${String(unlinked)} sem conta ligada.`
              : null}
          </p>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {list === null && !error ? <p>A carregar…</p> : null}
          {list && list.length === 0 ? (
            <p>Ainda não há prémios. Pode importá-los do site antigo.</p>
          ) : null}
          <ol className="flex flex-col gap-3">
            {list?.map((p, i) => {
              const heading = p.season !== lastSeason ? p.season : null;
              lastSeason = p.season;
              return (
                <li key={p.id} className="flex flex-col gap-2">
                  {heading !== null ? (
                    <h3 className="mt-2 font-display text-title text-gu-ink">{heading}</h3>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-4 border-2 border-gu-line p-2">
                    <HofMedal icon={p.icon} size={40} />
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="font-semibold">{p.name}</span>
                      <span className="text-caption text-gu-ink-muted">
                        {winnerLabel(p)}
                        {p.accountId === null && p.winnerName !== '' ? ' · sem conta' : ''}
                        {p.claim ? ` · pedido por ${p.claim.login}` : ''}
                      </span>
                      <span>
                        <StatusBadge tone={p.status === 'published' ? 'success' : 'neutral'}>
                          {p.status === 'published' ? 'No site' : 'Rascunho'}
                        </StatusBadge>
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy || !sameSeason(list[i - 1], p)}
                        aria-label={`Subir ${p.name} ${p.season}`}
                        onClick={() => void swap(i, i - 1)}
                      >
                        ↑
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy || !sameSeason(p, list[i + 1])}
                        aria-label={`Descer ${p.name} ${p.season}`}
                        onClick={() => void swap(i, i + 1)}
                      >
                        ↓
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => open(p.id)}>
                        Editar
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pedidos "e meu"
// ---------------------------------------------------------------------------

function useClaimActions(onDone: (p: AdminHofPrize) => void, onError: (m: string) => void) {
  const [busy, setBusy] = useState(false);
  const act = async (prize: AdminHofPrize, action: 'accept' | 'reject') => {
    setBusy(true);
    try {
      onDone(adminHofPrizeSchema.parse(await send('POST', `${BASE}/${prize.id}/claim/${action}`)));
    } catch (e) {
      onError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, act };
}

/** Membros que disseram no site "este premio e meu": o staff confirma ou recusa. */
function ClaimsCard({
  claims,
  onDone,
  onError,
}: {
  claims: AdminHofPrize[];
  onDone: (p: AdminHofPrize) => void;
  onError: (m: string) => void;
}) {
  const { busy, act } = useClaimActions(onDone, onError);
  return (
    <Card headingLevel={2} title={`Pedidos «É meu» (${String(claims.length)})`}>
      <ul className="flex flex-col gap-2">
        {claims.map((p) => (
          <li key={p.id} className="flex flex-wrap items-center gap-3 border-2 border-gu-line p-2">
            <HofMedal icon={p.icon} size={28} />
            <span className="min-w-0 flex-1">
              <span className="font-semibold">{p.claim?.login}</span> diz que é «{p.winnerName}»,{' '}
              {p.name} {p.season}
            </span>
            <Button size="sm" disabled={busy} onClick={() => void act(p, 'accept')}>
              Confirmar
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void act(p, 'reject')}>
              Recusar
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

const searchAccounts = (q: string) =>
  getJson(`${BASE}/accounts?q=${encodeURIComponent(q)}`, z.array(hofAccountSchema));

/** Procurar uma conta pelo login (tambem contas de jovens). */
function AccountPicker({ id, onSelect }: { id?: string; onSelect: (a: HofAccount) => void }) {
  const [q, setQ] = useState('');
  return (
    <Autocomplete
      id={id}
      value={q}
      onChange={setQ}
      search={searchAccounts}
      getKey={(a) => a.id}
      renderItem={(a) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-semibold">{a.login}</span>
          {a.kind === 'youth' ? (
            <span className="text-caption text-gu-ink-muted">conta de jovem</span>
          ) : null}
        </span>
      )}
      onSelect={(a) => {
        setQ('');
        onSelect(a);
      }}
      placeholder="Procurar pelo login…"
    />
  );
}

const thisYear = new Date().getFullYear();

function PrizeEditor({
  prize,
  names,
  onSaved,
  onClose,
}: {
  prize: AdminHofPrize | null;
  names: string[];
  onSaved: (p: AdminHofPrize) => void;
  onClose: () => void;
}) {
  const [season, setSeason] = useState(prize?.season ?? String(thisYear));
  const [name, setName] = useState(prize?.name ?? '');
  const [icon, setIcon] = useState<HofIcon>(prize?.icon ?? 'medalha');
  const [winnerName, setWinnerName] = useState(prize?.winnerName ?? '');
  const [account, setAccount] = useState<{ id: string; login: string } | null>(
    prize?.accountId ? { id: prize.accountId, login: prize.login ?? '(conta removida)' } : null,
  );
  const [reason, setReason] = useState(prize?.reason ?? '');
  const [photoUrl, setPhotoUrl] = useState<string | null>(prize?.photoUrl ?? null);
  const [photoAlt, setPhotoAlt] = useState(prize?.photoAlt ?? '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const images = useImagesBusy();
  const namesId = useId();
  const claims = useClaimActions((p) => {
    onSaved(p);
    if (p.accountId) setAccount({ id: p.accountId, login: p.login ?? '' });
  }, setError);

  const isPublished = prize?.status === 'published';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      season: season.trim(),
      name,
      icon,
      winnerName,
      accountId: account?.id ?? null,
      reason,
      photoUrl,
      photoAlt: photoUrl ? photoAlt : '',
    };
    setBusy(true);
    setError(null);
    setSaved(null);
    setFields({});
    try {
      const res = prize
        ? await send('PUT', `${BASE}/${prize.id}`, payload)
        : await send('POST', BASE, payload);
      onSaved(adminHofPrizeSchema.parse(res));
      setSaved(prize ? 'Guardado.' : 'Criado como rascunho.');
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  const run = async (path: string) => {
    if (!prize) return;
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      onSaved(adminHofPrizeSchema.parse(await send('POST', `${BASE}/${prize.id}${path}`)));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!prize) return;
    if (!window.confirm(`Apagar «${prize.name} ${prize.season}»? Não se pode desfazer.`)) return;
    setBusy(true);
    try {
      await send('DELETE', `${BASE}/${prize.id}`);
      onClose();
    } catch (e) {
      setError(messageOf(e));
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          ← Hall of Fame
        </Button>
      </div>
      <Card headingLevel={2} title={prize ? `${prize.name} ${prize.season}` : 'Novo prémio'}>
        <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
          {prize?.claim ? (
            <Notice tone="warning" title="Pedido «É meu»">
              <span>
                A conta <strong>{prize.claim.login}</strong> diz que este prémio é seu.{' '}
              </span>
              <span className="mt-2 flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={claims.busy}
                  onClick={() => void claims.act(prize, 'accept')}
                >
                  Confirmar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={claims.busy}
                  onClick={() => void claims.act(prize, 'reject')}
                >
                  Recusar
                </Button>
              </span>
            </Notice>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-[10rem_1fr_12rem]">
            <Field label="Ano" hint="2025 ou 2020-21" error={fields.season}>
              <Input
                inputMode="numeric"
                maxLength={7}
                value={season}
                onChange={(e) => setSeason(e.target.value.replace(/[^\d-]/g, ''))}
              />
            </Field>
            <Field label="Prémio" hint="Ex.: «uP Player»." error={fields.name}>
              <Input
                list={namesId}
                value={name}
                maxLength={80}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <datalist id={namesId}>
              {names.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
            <div className="flex items-start gap-2">
              <HofMedal icon={icon} size={40} className="mt-8" />
              <Field
                label="Desenho"
                hint="A medalha no site e na carta."
                error={fields.icon}
                className="flex-1"
              >
                <Select value={icon} onChange={(e) => setIcon(hofIconSchema.parse(e.target.value))}>
                  {HOF_ICONS.map((i) => (
                    <option key={i} value={i}>
                      {HOF_ICON_LABELS[i]}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Vencedor" hint="O nome como aparece no site." error={fields.winnerName}>
              <Input
                value={winnerName}
                maxLength={120}
                onChange={(e) => setWinnerName(e.target.value)}
              />
            </Field>
            <Field
              label="Conta"
              optional
              hint="Ligada, o prémio aparece na carta de membro. Quem hoje é menor aparece só com o login."
              error={fields.accountId}
            >
              {account ? (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{account.login}</span>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setAccount(null)}>
                    Desligar
                  </Button>
                </div>
              ) : (
                <AccountPicker
                  onSelect={(a) => {
                    setAccount({ id: a.id, login: a.login });
                    if (winnerName === '') setWinnerName(a.login);
                  }}
                />
              )}
            </Field>
          </div>
          <Field label="Frase" optional hint="Uma frase sobre o porquê." error={fields.reason}>
            <Textarea
              rows={2}
              value={reason}
              maxLength={300}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <div className="grid items-start gap-3 sm:grid-cols-[16rem_1fr]">
            <ImageField
              label="Foto"
              optional
              value={photoUrl}
              onChange={setPhotoUrl}
              aspect={16 / 9}
              onBusyChange={images.track('foto')}
              error={fields.photoUrl}
              hint="Da entrega do prémio. Não aparece se o vencedor for menor."
            />
            {photoUrl ? (
              <Field label="Descrição da foto" hint="Para quem não a vê." error={fields.photoAlt}>
                <Input
                  value={photoAlt}
                  maxLength={300}
                  onChange={(e) => setPhotoAlt(e.target.value)}
                />
              </Field>
            ) : null}
          </div>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {saved ? <Notice tone="success">{saved}</Notice> : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={busy || images.busy}>
              {prize ? 'Guardar' : 'Criar rascunho'}
            </Button>
            {prize && !isPublished ? (
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => void run('/publish')}
              >
                Publicar
              </Button>
            ) : null}
            {isPublished ? (
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => void run('/unpublish')}
              >
                Tirar do site
              </Button>
            ) : null}
            {prize && !isPublished ? (
              <Button type="button" variant="ghost" disabled={busy} onClick={() => void remove()}>
                Apagar
              </Button>
            ) : null}
          </div>
          {prize ? (
            <p className="text-caption text-gu-ink-muted">
              {isPublished ? (
                <>
                  No site.{' '}
                  <a
                    className="gu-link"
                    href={`${moduleOrigins().site}/hall-of-fame`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Ver no site
                  </a>
                  .{' '}
                </>
              ) : (
                'Rascunho: ainda não aparece no site. '
              )}
              Última alteração: {formatDateTime(prize.updatedAt)} por {prize.updatedBy}.
            </p>
          ) : null}
        </form>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Importar do site antigo
// ---------------------------------------------------------------------------

function ImportView({ onClose }: { onClose: () => void }) {
  const [preview, setPreview] = useState<HofImportResult | null>(null);
  const [done, setDone] = useState<HofImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(dryRun: boolean) {
    setBusy(true);
    setError(null);
    try {
      const result = hofImportResultSchema.parse(await send('POST', `${BASE}/import`, { dryRun }));
      if (dryRun) setPreview(result);
      else setDone(result);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  const toImport = preview?.seasons.filter((s) => !s.skipped) ?? [];
  const awards = toImport.reduce((n, s) => n + s.awards.length, 0);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          ← Hall of Fame
        </Button>
      </div>
      <Card headingLevel={2} title="Importar do site antigo">
        <div className="flex flex-col gap-4">
          <p className="text-gu-ink-muted">
            Lê a página{' '}
            <a className="gu-link" href={HOF_LEGACY_URL} target="_blank" rel="noopener noreferrer">
              {HOF_LEGACY_URL}
            </a>{' '}
            e mostra primeiro o que vai importar. Os anos que já têm prémios aqui ficam como estão.
            Os prémios entram já publicados, com os vencedores escritos à mão; depois ligue cada um
            à sua conta.
          </p>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {done ? (
            <Notice tone="success">
              Importado: {done.seasons.filter((s) => !s.skipped).length} anos.{' '}
              <Button size="sm" variant="ghost" onClick={onClose}>
                Ver a lista →
              </Button>
            </Notice>
          ) : null}
          {!done ? (
            <div className="flex gap-2">
              <Button variant="secondary" disabled={busy} onClick={() => void run(true)}>
                {preview ? 'Ler outra vez' : 'Ver o que vai ser importado'}
              </Button>
              {preview && toImport.length > 0 ? (
                <Button disabled={busy} onClick={() => void run(false)}>
                  Importar {toImport.length} anos ({awards} prémios)
                </Button>
              ) : null}
            </div>
          ) : null}
          {busy ? <p className="text-gu-ink-muted">A ler o site antigo…</p> : null}
          {preview ? <ImportPreview result={done ?? preview} /> : null}
        </div>
      </Card>
    </div>
  );
}

function ImportPreview({ result }: { result: HofImportResult }) {
  return (
    <div className="flex flex-col gap-3">
      {result.seasons.map((s) => (
        <details key={s.label} className="border border-gu-line p-3">
          <summary className="cursor-pointer">
            <span className="font-display text-gu-ink">{s.label}</span> · {s.awards.length} prémios
            {s.skipped ? ' · já existe, fica como está' : ''}
          </summary>
          <ul className="mt-2 flex flex-col gap-1 text-caption">
            {s.awards.map((a, i) => (
              <li key={`${a.name}-${String(i)}`}>
                <span className="text-gu-ink">{a.name}</span> —{' '}
                {a.winners.length > 0 ? a.winners.join(' · ') : <em>sem vencedor</em>}
              </li>
            ))}
          </ul>
        </details>
      ))}
      {result.ignored.length > 0 ? (
        <Notice tone="warning" title="Linhas que não percebi (não entram)">
          <ul className="list-disc pl-5">
            {result.ignored.map((l, i) => (
              <li key={`${String(i)}-${l}`}>{l}</li>
            ))}
          </ul>
        </Notice>
      ) : null}
    </div>
  );
}
